import { MouseEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { Browser } from '@capacitor/browser';
import { CameraPlayer } from 'features/cameras/components/CameraPlayer';
import { CameraPreview } from 'features/cameras/components/CameraPreview';
import {
  useAlertCamera,
  useAlertCameras,
} from 'features/cameras/hooks/useAlertCameras';
import { AlertCamera } from 'features/cameras/cameras.types';
import { InlineStatus } from 'components/InlineStatus';
import { Map } from 'features/mapping/components/Map';
import { MapType } from 'features/mapping/mapTypePolicy';
import { AlertCamerasLayer } from 'features/mapping/components/layers/AlertCamerasLayer';
import { WildfiresLayer } from 'features/mapping/components/layers/WildfiresLayer';
import {
  isTimelapseSupported,
  getCameraAttributionImage,
  getRelatedCameras,
  getCameraAttributionLabelTransKey,
} from 'features/cameras/cameras.utils';
import { CameraAttributionLogo } from 'features/cameras/components/CameraAttributionLogo';
import { OfflineMessage } from 'features/cameras/components/OfflineMessage';
import { Helmet } from 'react-helmet';
import { useWildfires } from 'features/wildfires/hooks/useWildfires';
import { CAMERA_ATTRIBUTION_LOGO_URL_DARK } from 'features/cameras/cameras.constants';
import { useCameraCommandPaletteActions } from 'hooks/commandPaletteActions';

type CameraProps = {
  camera: AlertCamera;
};

type StyleProps = {
  isTablet: boolean;
};

const useStyles = makeStyles<StyleProps>()((theme, { isTablet }) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    overflowX: 'hidden',
    width: '100%',
    height: '100%',
    padding: theme.spacing(2),
  },
  container: {
    display: 'grid',
    gridTemplateColumns: 'minmax(50vmin, 125vmin) minmax(280px, 1fr)',
    gap: '16px',
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
    },
    '@media (max-height: 576px)': {
      gridTemplateColumns: '1fr 1fr',
    },
    '@media (min-width: 2000px) and (max-height: 1000px)': {
      // For larger screens, should we even bother?
      gridTemplateColumns: '1fr 1fr',
    },
  },
  map: {
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    width: '100%',
    height: 282,
  },
  relatedCamerasGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: `repeat(${isTablet ? 4 : 3}, 1fr)`,
  },
}));

const Camera = (props: CameraProps): JSX.Element => {
  const { camera } = props;

  if (camera.isOffline) {
    return <OfflineMessage camera={camera} />;
  }

  if (isTimelapseSupported(camera)) {
    return (
      <CameraPlayer
        // key prop needed to force component to remount when cameraId
        // changes so the component's local state is reset; useful when
        // switching between related cameras
        key={camera.id}
        camera={camera}
        fullScreen
      />
    );
  }

  return <CameraPreview url={camera.imageUrl} />;
};

export const CameraFullscreenDetails = (): JSX.Element => {
  const { cameraId } = useParams<{ cameraId: string }>();
  const isTablet = useMediaQuery((theme) => theme.breakpoints.up('tablet'));
  const { classes } = useStyles({ isTablet });
  const { t } = useTranslation();
  const { alertCamera, isLoading, error } = useAlertCamera({
    id: cameraId,
    refetchInterval: 1000 * 15,
  });
  const {
    alertCameras: allCameras,
    isLoading: isLoadingAll,
    error: allErrors,
  } = useAlertCameras({ enabled: true });
  const { wildfireEvents } = useWildfires();

  const relatedCameras =
    alertCamera && allCameras?.length
      ? getRelatedCameras(t, alertCamera, allCameras)
      : [];

  useCameraCommandPaletteActions(cameraId, relatedCameras, true);

  const attributionImage = alertCamera
    ? getCameraAttributionImage(alertCamera, CAMERA_ATTRIBUTION_LOGO_URL_DARK)
    : '';

  const handleClickAttribution = async (
    event: MouseEvent<HTMLAnchorElement>,
  ): Promise<void> => {
    event.preventDefault();

    if (!alertCamera) return;

    await Browser.open({
      url: alertCamera.cameraUrl,
      presentationStyle: 'popover',
    });
  };

  return (
    <>
      {alertCamera && (
        <Helmet>
          <title>
            {t('home.alertCameraTitle', { name: alertCamera.name })}
          </title>
        </Helmet>
      )}

      <div className={classes.root}>
        <Box className={classes.container}>
          {alertCamera && (
            <Box sx={{ alignSelf: 'start' }}>
              <Camera camera={alertCamera} />
            </Box>
          )}

          <Grid container spacing={4} sx={{ width: '100%' }}>
            <Grid size={12}>
              <Grid container spacing={1} sx={{ width: '100%' }}>
                <Grid size={12}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 'bold',
                    }}
                  >
                    {alertCamera?.name}
                  </Typography>
                </Grid>

                {alertCamera && (
                  <Grid size={12}>
                    <div className={classes.map}>
                      <Map
                        center={alertCamera.latlng}
                        zoom={10}
                        mapType={MapType.CameraDetails}
                      >
                        <AlertCamerasLayer
                          visible
                          interactive={false}
                          drawerIsOpen={false}
                        />
                        <WildfiresLayer
                          wildfires={wildfireEvents}
                          isFadable
                          interactive={false}
                        />
                      </Map>
                    </div>
                  </Grid>
                )}

                {alertCamera && relatedCameras.length > 1 && (
                  <>
                    <Grid size={12}>
                      <Typography
                        id="additional-cameras-header"
                        variant="body1"
                        component="h4"
                        sx={{
                          fontWeight: 'bold',
                        }}
                      >
                        {t('alertCameras.additionalCameras')}
                      </Typography>
                    </Grid>

                    <Grid size={12}>
                      <ToggleButtonGroup
                        value={alertCamera.id}
                        variant="watchduty-gap"
                        aria-labelledby="additional-cameras-header"
                        className={classes.relatedCamerasGrid}
                      >
                        {relatedCameras?.map((camera: AlertCamera) => (
                          <ToggleButton
                            color="primary"
                            key={camera.id}
                            value={camera.id}
                            size="large"
                            component={Link}
                            to={`/camera/${camera.id}/fullscreen`}
                            sx={{ minWidth: 48 }}
                          >
                            {camera.name}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>

            {alertCamera && attributionImage && (
              <Grid
                size={12}
                sx={{
                  alignSelf: 'flex-end',
                }}
              >
                <Stack spacing={2} alignItems="center">
                  <Typography
                    variant="subtitle1"
                    color="text.primary"
                    sx={{
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      fontWeight: 'bold',
                    }}
                  >
                    {alertCamera &&
                      t(getCameraAttributionLabelTransKey(alertCamera))}
                  </Typography>

                  <CameraAttributionLogo
                    src={attributionImage}
                    alt={alertCamera.attribution}
                    href={alertCamera.cameraUrl}
                    attribution={alertCamera.attribution}
                    onClick={handleClickAttribution}
                  />
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>

        {(isLoading || isLoadingAll || error || allErrors) && (
          <InlineStatus
            status={isLoading || isLoadingAll ? 'loading' : 'error'}
            progressColor="primary"
          />
        )}
      </div>
    </>
  );
};
